import * as THREE from 'three';
import {
  ATLAS_CELL_PX,
  ATLAS_CELLS,
  ATLAS_PX,
  ATLAS_SIDE,
  hexToRgb,
} from '../palette';

/* ═══════════════════════════════════════════════════════════════════════════
   LE matériau des coques — un seul, pour toute la flotte.

   Le plan (§4.8) exige que le catalogue `src/view/materials/` soit FERMÉ : le
   nombre de permutations de shaders doit être borné, sinon le warm-up ne peut
   rien garantir. La conséquence pratique est qu'on ne crée pas un matériau par
   faction, ni par archétype, ni par niveau.

   Ce qui rend un matériau unique possible malgré vingt-deux silhouettes et
   seize couleurs : **la couleur est une coordonnée de texture**, pas un
   paramètre de matériau. Un sommet porte l'UV du centre de sa cellule dans
   l'atlas de palette ; changer la peinture d'une facette est un changement de
   GÉOMÉTRIE, gratuit à l'exécution.

   Trois greffes seulement, toutes dans un unique `onBeforeCompile` :

     1. Le masque d'ÉMISSION, lu dans le canal alpha de l'atlas.
     2. Le LISERÉ de fresnel — l'effet signature (§4.6).
     3. La remise à 1 de l'alpha, sans quoi le point 1 rendrait les coques
        semi-transparentes.
   ═══════════════════════════════════════════════════════════════════════════ */

/**
 * Construit l'atlas de palette en mémoire.
 *
 * Aucun octet téléchargé, aucun décodage PNG, aucun transcodeur : 16 Ko de
 * VRAM générés en quelques microsecondes au démarrage.
 *
 * ⚠️ `NearestFilter` et `generateMipmaps = false` sont OBLIGATOIRES. Avec un
 * filtrage linéaire, un fragment tombant à cheval sur deux cellules
 * interpolerait entre le gris de coque et l'ambre d'accent : on verrait des
 * pixels orange isolés sur les silhouettes, sans jamais penser à un demi-texel.
 * Avec des mipmaps, le problème se produirait dès que le vaisseau s'éloigne, et
 * au niveau le plus grossier TOUTE la flotte prendrait la couleur moyenne de la
 * palette.
 */
export function buildPaletteAtlas(): THREE.DataTexture {
  const data = new Uint8Array(ATLAS_PX * ATLAS_PX * 4);

  for (let i = 0; i < ATLAS_CELLS.length; i++) {
    const cell = ATLAS_CELLS[i]!;
    const [r, g, b] = hexToRgb(cell.hex);
    const col = i % ATLAS_SIDE;
    const row = Math.floor(i / ATLAS_SIDE);
    const x0 = col * ATLAS_CELL_PX;
    const y0 = row * ATLAS_CELL_PX;

    for (let y = 0; y < ATLAS_CELL_PX; y++) {
      for (let x = 0; x < ATLAS_CELL_PX; x++) {
        /* ⚠️ L'origine d'une DataTexture est EN BAS. `cellUv()` calcule
           `1 - (row + 0.5) / grid`, ce qui suppose une origine en haut : les
           deux conventions doivent se rencontrer ici, une fois, et pas dans le
           shader. Se tromper repeint la flotte avec les cellules de la ligne
           opposée — un résultat parfaitement plausible à l'écran, donc très
           long à diagnostiquer. */
        const py = ATLAS_PX - 1 - (y0 + y);
        const o = (py * ATLAS_PX + (x0 + x)) * 4;
        data[o] = Math.round(r * 255);
        data[o + 1] = Math.round(g * 255);
        data[o + 2] = Math.round(b * 255);
        // Le canal alpha porte le MASQUE D'ÉMISSION, pas la transparence.
        data[o + 3] = Math.round(cell.glow * 255);
      }
    }
  }

  const tex = new THREE.DataTexture(data, ATLAS_PX, ATLAS_PX);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.magFilter = THREE.NearestFilter;
  tex.minFilter = THREE.NearestFilter;
  tex.generateMipmaps = false;
  tex.needsUpdate = true;
  return tex;
}

/** Liseré : teinte et puissance. Un bleu-acier froid, jamais la couleur de
 *  faction — l'allégeance est portée par les cellules émissives de l'atlas, et
 *  garder le liseré neutre est ce qui permet de n'avoir qu'un seul matériau. */
export const HULL_RIM_COLOR = new THREE.Color(0x7fa8c8);
export const HULL_RIM_STRENGTH = 0.42;
/** Gain appliqué au masque d'émission. Au-delà de 1,0 en HDR, une facette
 *  franchit le seuil de bloom (0,85) et se met à rayonner — c'est le seul
 *  mécanisme par lequel quoi que ce soit brille dans ce jeu (§4.6).
 *
 *  Relevé de 2,6 à 3,6 sur retour de jeu (« ça manque d'effet lumineux ») :
 *  2,6 posait les accents ambre juste au-dessus du seuil, donc un halo à peine
 *  perceptible. 3,6 reste dans la plage prescrite (2,0 à 8,0) mais donne aux
 *  accents et aux tuyères cyan un rayonnement qui se voit vraiment, sans les
 *  faire déborder en aplat. Valeur de goût : à confirmer à l'œil dans [3,0 ; 5,0]. */
export const HULL_GLOW_GAIN = 3.6;

/**
 * Le matériau unique. Construit une fois ; il n'existe aucune variante.
 */
export function makeHullMaterial(atlas: THREE.Texture): THREE.MeshStandardMaterial {
  const mat = new THREE.MeshStandardMaterial({
    map: atlas,
    color: 0xffffff,
    // Métallique et plutôt lisse : c'est ce qui donne à la PMREM de 64 px de
    // quoi répondre. Sans métalité, l'environment map ne se voit pas et le
    // rendu redevient plat — le défaut exact que §4.6 signale.
    metalness: 0.6,
    roughness: 0.42,
    envMapIntensity: 1.5,
    emissive: 0x000000,
    // ⚠️ Opaque. Le canal alpha de l'atlas sert de masque d'émission ; activer
    // la transparence trierait les coques par profondeur pour rien et les
    // rendrait effectivement translucides là où elles doivent briller.
    transparent: false,
    flatShading: false,
  });

  mat.onBeforeCompile = (shader) => {
    shader.uniforms['uRimColor'] = { value: HULL_RIM_COLOR };
    shader.uniforms['uRimStrength'] = { value: HULL_RIM_STRENGTH };
    shader.uniforms['uGlowGain'] = { value: HULL_GLOW_GAIN };

    shader.fragmentShader = shader.fragmentShader
      .replace(
        '#include <common>',
        `#include <common>
uniform vec3 uRimColor;
uniform float uRimStrength;
uniform float uGlowGain;
float hullGlowMask;`,
      )
      /* Le masque d'émission se lit IMMÉDIATEMENT après l'échantillonnage de
         l'atlas, avant `<color_fragment>` qui multiplie la couleur d'instance.
         L'ordre compte : la couleur d'instance porte le clignotement de dégât
         et le marqueur de télégraphe, et un ennemi entamé ne doit pas voir ses
         accents s'éteindre parce qu'on l'a éclairci. */
      .replace(
        '#include <map_fragment>',
        `#include <map_fragment>
hullGlowMask = diffuseColor.a;
diffuseColor.a = 1.0;`,
      )
      /* L'émission est proportionnelle à la couleur de la facette : une cellule
         ambre rayonne ambre, une tuyère cyan rayonne cyan. On n'a donc jamais à
         maintenir une seconde palette d'émissifs en parallèle de la première —
         c'est la source d'incohérence classique quand les deux dérivent. */
      .replace(
        '#include <emissivemap_fragment>',
        `#include <emissivemap_fragment>
totalEmissiveRadiance += diffuseColor.rgb * hullGlowMask * uGlowGain;`,
      )
      /* ⚠️ Le liseré s'injecte AVANT `<opaque_fragment>`, où `outgoingLight`
         existe déjà et n'a pas encore été écrit dans `gl_FragColor`. C'est le
         même piège que sur le matériau de projectile : injecter plus tard
         compile parfaitement et ne fait rien. */
      .replace(
        '#include <opaque_fragment>',
        `float hullFresnel = pow(1.0 - abs(dot(normalize(vViewPosition), normal)), 2.2);
outgoingLight += uRimColor * hullFresnel * uRimStrength;
#include <opaque_fragment>`,
      );
  };

  /* ⚠️ Sans clé de cache distincte, three réutiliserait le programme déjà
     compilé pour un MeshStandardMaterial ordinaire et les trois greffes
     seraient ignorées EN SILENCE — le shader compile, la géométrie est juste,
     et seule la lumière est fausse. Symptôme le plus déroutant de three.js.

     La clé est CONSTANTE : il n'existe qu'une variante de coque, donc un seul
     programme, et le compteur du warm-up ne bougera jamais à cause d'elle. */
  mat.customProgramCacheKey = () => 'hull-v1';

  return mat;
}

/**
 * Le contour par coque inversée (PLAN.md §4.6) — réservé aux boss et aux élites.
 *
 * Une seconde passe de la même géométrie, légèrement gonflée le long des
 * normales et rendue en `BackSide` : il ne reste visible que la mince bordure
 * qui dépasse. Coût : un draw call par archétype concerné, pas par instance.
 *
 * Ce que ça achète : la silhouette d'un boss reste lisible même posée devant un
 * décor de même valeur — ce qui est précisément le cas quand la chaîne de post
 * a fini de tout harmoniser.
 */
export function makeOutlineMaterial(): THREE.ShaderMaterial {
  const mat = new THREE.ShaderMaterial({
    uniforms: { uWidth: { value: 0.035 }, uColor: { value: new THREE.Color(0x0a0e14) } },
    vertexShader: /* glsl */ `
#include <common>
uniform float uWidth;
void main() {
  vec3 inflated = position + normal * uWidth;
  #ifdef USE_INSTANCING
    vec4 mv = modelViewMatrix * instanceMatrix * vec4(inflated, 1.0);
  #else
    vec4 mv = modelViewMatrix * vec4(inflated, 1.0);
  #endif
  gl_Position = projectionMatrix * mv;
}`,
    fragmentShader: /* glsl */ `
precision mediump float;
uniform vec3 uColor;
void main() { gl_FragColor = vec4(uColor, 1.0); }`,
    side: THREE.BackSide,
  });
  // Un ShaderMaterial n'a pas de variante à distinguer, mais la clé rend le
  // programme identifiable dans le compte du warm-up.
  mat.customProgramCacheKey = () => 'hull-outline-v1';
  return mat;
}
